import { BLEND_MODES, Sprite } from 'pixi.js'
import Layer from '@/scripts/canvas/Layer'

type VehiclePoint = { id: string; x: number; y: number }

export function createVehicleWithLight(
  layer: Layer,
  vehicle: VehiclePoint,
  light?: VehiclePoint,
  wheel?: VehiclePoint | VehiclePoint[],
  sign?: VehiclePoint,
  options?: {
    wheelOffset?: { x: number; y: number; rotation?: number }[]
    signOffset?: { x: number; y: number }
  },
): Sprite {
  const vehicleSprite = Sprite.from(`${vehicle.id}.png`)
  vehicleSprite.position.set(vehicle.x, vehicle.y)
  vehicleSprite.zIndex = 10
  vehicleSprite.eventMode = 'passive'
  vehicleSprite.pivot.set(vehicleSprite.width / 2, 0)
  layer.addChild(vehicleSprite)

  if (light) {
    const dx = light.x - vehicle.x
    const dy = light.y - vehicle.y
    const lightSprite = Sprite.from(`${light.id}.png`)
    lightSprite.position.set(dx, dy)
    lightSprite.blendMode = BLEND_MODES.ADD
    lightSprite.alpha = 0.8
    lightSprite.zIndex = 11
    lightSprite.eventMode = 'none'
    vehicleSprite.addChild(lightSprite)
  }

  // wheel (with manual offset)
  if (wheel && Array.isArray(wheel)) {
    wheel.forEach((w, i) => {
      const dx = w.x - vehicle.x + (options?.wheelOffset?.[i]?.x ?? 0)
      const dy = w.y - vehicle.y + (options?.wheelOffset?.[i]?.y ?? 0)
      const rotation = options?.wheelOffset?.[i]?.rotation ?? 0

      const wheelSprite = Sprite.from(`${w.id}.png`)
      wheelSprite.position.set(dx, dy)
      wheelSprite.anchor.set(0.5)
      wheelSprite.rotation = rotation
      wheelSprite.zIndex = 12
      wheelSprite.eventMode = 'none'
      wheelSprite.name = `wheel_${i}` // so animateCar can find them all
      vehicleSprite.addChild(wheelSprite)
    })
  } else if (wheel) {
    // single wheel fallback (still works)
    const dx = wheel.x - vehicle.x + (options?.wheelOffset?.[0]?.x ?? 0)
    const dy = wheel.y - vehicle.y + (options?.wheelOffset?.[0]?.y ?? 0)
    const rotation = options?.wheelOffset?.[0]?.rotation ?? 0

    const wheelSprite = Sprite.from(`${wheel.id}.png`)
    wheelSprite.position.set(dx, dy)
    wheelSprite.anchor.set(0.5)
    wheelSprite.rotation = rotation
    wheelSprite.zIndex = 12
    wheelSprite.eventMode = 'none'
    wheelSprite.name = 'wheel_0'
    vehicleSprite.addChild(wheelSprite)
  }

  // sign (with manual offset)
  if (sign) {
    const dx = sign.x - vehicle.x + (options?.signOffset?.x ?? 0)
    const dy = sign.y - vehicle.y + (options?.signOffset?.y ?? 0)
    const signSprite = Sprite.from(`${sign.id}.png`)
    signSprite.position.set(dx, dy)
    signSprite.zIndex = 12
    signSprite.eventMode = 'none'
    signSprite.name = 'sign'
    vehicleSprite.addChild(signSprite)
  }

  return vehicleSprite
}

export function flipVehicle(sprite: Sprite, flip: boolean, hasSign = false): void {
  // flip the whole vehicle
  sprite.scale.x = flip ? -1 : 1

  if (!hasSign) return

  const sign = sprite.getChildByName('sign') as Sprite | undefined
  if (sign) {
    sign.scale.x = flip ? -1 : 1
    // TODO: Remove manual positioning of sign when flipped
    // TODO: Find out why this block is not running on the first car drive
    sign.x = flip ? 195 : 100
  }
}
